import { AxiosError } from "axios";
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signInWithPopup,
  updateProfile,
  verifyPasswordResetCode,
} from "firebase/auth";
import { ISaveDataEvent, ISignInInput, ISignUpInput } from "./types";
import { app } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { currentHost, getGeneralErrorMessage } from "../../utils/API/apiHelper";
import {
  clearMessage,
  setLoading,
  setMessage,
} from "../../store/slices/authSlice";
import { useAppDispatch } from "../../store";
import { apiClient } from "../../utils/API/axios.interceptor";
import { endpoints } from "../../../endpoints";
import { getEnvVariable } from "../../env.utils";

const errorMessages = {
  "auth/invalid-credential": "Invalid email or password",
  "auth/user-not-found":
    "It looks like that email is not associated with an account, please try signing up with a new email.",
  "auth/wrong-password": "Invalid email or password",
  "auth/too-many-requests":
    "Too many failed login attempts. Please try again later.",
  "auth/network-request-failed":
    "Network request failed. Please check your internet connection and try again.",
  "auth/user-disabled":
    "Your account has been disabled. Please contact support.",
  "auth/account-exists-with-different-credential":
    "This email is associated with a different sign-in method. Please try another sign-in option.",
  "auth/email-already-in-use":
    "Welcome back! This email is already registered. Please sign in to continue.",
  "auth/invalid-email": "Invalid email format.",
  "auth/weak-password":
    "Password is too weak. Please choose a stronger password.",
  "auth/operation-not-allowed":
    "Operation not allowed. Please contact support.",
};

export const useLoginApi = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleAuthError = async (
    error: AxiosError<{ message: string; status: boolean }> | any
  ) => {
    dispatch(setLoading(false));

    if (error.code && errorMessages[error.code as keyof typeof errorMessages]) {
      dispatch(
        setMessage({
          value: errorMessages[error.code as keyof typeof errorMessages],
          status: "error",
        })
      );
    } else if (error.response?.data.status === false) {
      dispatch(
        setMessage({ value: error.response.data?.message, status: "error" })
      );
    } else {
      const message = await getGeneralErrorMessage(error);
      dispatch(setMessage({ value: message, status: "error" }));
    }
  };

  const saveUserData = async ({ event }: { event: ISaveDataEvent }) => {
    try {
      const userPreferences = sessionStorage.getItem("selectedValues");
      await apiClient.post(endpoints.auth.saveUserData, {
        userPreferences: JSON.parse(userPreferences as string),
        event,
      });
    } catch (error: AxiosError<{ message: string; status: boolean }> | any) {
      throw error;
    }
  };

  const loginWithEmailAndPassword = async (data: ISignInInput) => {
    dispatch(clearMessage());
    dispatch(setLoading(true));
    try {
      const auth = getAuth(app);
      await signInWithEmailAndPassword(auth, data.email, data.password);
      await saveUserData({ event: "LOGIN" });
      navigate("/dashboard/learn");
    } catch (error: AxiosError<{ message: string; status: boolean }> | any) {
      await handleAuthError(error);
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const signupWithEmailAndPassword = async (data: ISignUpInput) => {
    dispatch(setMessage(null));
    dispatch(setLoading(true));
    try {
      const auth = getAuth(app);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      const user = userCredential.user;

      await updateProfile(user, {
        displayName: data.name,
      });

      await saveUserData({ event: "SIGNUP" });
      navigate("/dashboard/learn");
    } catch (error: AxiosError<{ message: string; status: boolean }> | any) {
      await handleAuthError(error);
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSocialSignInSignUp = async (
    providerName: string,
    event: ISaveDataEvent
  ) => {
    const provider = new OAuthProvider(providerName);
    const auth = getAuth(app);

    dispatch(setLoading(true));
    dispatch(clearMessage());

    try {
      const isDevelopment = getEnvVariable("VITE_MODE") == "development";
      if (isDevelopment) {
        await signInWithPopup(auth, provider);
      } else {
        await signInWithRedirect(auth, provider);
      }
      await saveUserData({ event });
      navigate("/dashboard/learn");
    } catch (error) {
      await handleAuthError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const forgetpassword = async (payload: any) => {
    dispatch(setMessage(null));
    dispatch(setLoading(true));
    const auth = getAuth();
    try {
      await sendPasswordResetEmail(auth, payload.email, {
        url: `${currentHost}/reset-password`,
        handleCodeInApp: true,
      });
      dispatch(
        setMessage({
          value:
            "If an account exists with this email, a password reset link has been sent.",
          status: "success",
        })
      );
    } catch (error) {
      await handleAuthError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const verifyResetCode = async (oobCode: string): Promise<void> => {
    const auth = getAuth();
    dispatch(setMessage(null));
    dispatch(setLoading(true));
    try {
      await verifyPasswordResetCode(auth, oobCode);
    } catch (error: any) {
      if (
        error?.code === "auth/invalid-action-code" ||
        error?.code === "auth/expired-action-code"
      ) {
        setMessage({
          value: "Invalid reset link. Please request a new password reset.",
          status: "error",
        });
      } else {
        setMessage({
          value:
            "An error occurred while verifying the reset code. Please try again.",
          status: "error",
        });
      }
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const resetPassword = async (
    oobCode: string,
    newPassword: string
  ): Promise<void> => {
    const auth = getAuth();
    dispatch(setMessage(null));
    dispatch(setLoading(true));
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
    } catch (error: any) {
      if (error?.code === "auth/weak-password") {
        setMessage({
          value: "Password is too weak. Please choose a stronger password.",
          status: "error",
        });
      } else {
        setMessage({
          value: "Failed to reset password. Please try again.",
          status: "error",
        });
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  return {
    loginWithEmailAndPassword,
    signupWithEmailAndPassword,
    handleSocialSignInSignUp,
    saveUserData,
    handleAuthError,
    forgetpassword,
    resetPassword,
    verifyResetCode,
  };
};
